import { LoadingButton, TabContext, TabList } from "@mui/lab";
import {
  Box,
  CircularProgress,
  Menu,
  MenuItem,
  Skeleton,
  Stack,
  Tab,
  Typography,
} from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import compInfos from "./broadcasting-table.json";
import * as XLSX from "xlsx";

import { Link, useNavigate, useParams } from "react-router-dom";
import { useLangContext } from "../../../../context/langContext";

import { dateFormatter } from "../../../../utils/date-formatter";
import {
  BroadCastingNumberStatusEnum,
  CallDetailsSchema,
} from "../../../../services/Schemas/Broadcast";
import MuiTabPanel from "../../../common/tab-panel/TabPanel";
import CustomTable from "../../../common/tables/Table";
import { useProtectedAxiosInstance } from "../../../../hooks/axiosInstance";
import { useNotificationContext } from "../../../../context/notificationContext";
import {
  BroadcastExportListFilters,
  BroadcastingDetailsRequest,
} from "../../../../services/Endpoints/broadcast";
import errorResolver from "../../../../utils/error-resolver";
import { statusInfo } from "../../../../constants/status";
import InfoBadge, { OriginEnum } from "../../../common/info-badge/InfoBadge";
import ViewAction from "../../../common/view-action/ViewAction";
import MuiBaseModal from "../../../common/modal/MuiModal";
import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../../services/Schemas/ListQueryString";
import {
  FilterList,
  NumberListColumnFilterable,
  NumberListRequest,
} from "../../../../services/Endpoints/broadcast/NumbersEndpoint";
import {
  CallDetailListColumnFilterable,
  CallDetailListColumnSortable,
  CallDetailListRequest,
} from "../../../../services/Endpoints/broadcast/PbxCallDetails";
import FileDownloadRounded from "@mui/icons-material/FileDownloadRounded";
import { ITableSchema } from "../../../../utils/types";
import { PartnerSchema } from "../../../../services/Schemas/Partner";
import LinkWithPopover from "../../common/LinkWithPopover";
import { PartnerListColumnFilterable } from "../../../../services/Endpoints/partners/PartnerEndpoint";
import { routerPaths } from "../../../../constants/Paths";
import { useUrl } from "../../../../hooks/urlEncoding";

interface ICallDetail {
  id: number;
  number: string;
  duration_seconds: number;
  answer_time: string | null;
  pbx_server_id: number | null;
  pbx_trunk_id: number | null;
  extension: string | null;
  voice_broadcast_id: number;
  max_pending_calls?: number;
  created_at: string;
  status: ReactNode;
  operations: string | ReactNode;
  updated_at: string | null;
  pickup_time: string | null;
  sent_time: string | null;
  end_time: string | null;
  caller_id: string;
  partner: null | (Partial<PartnerSchema> & { id?: number; caption?: string });
}

const displayedData = {
  id: "آیدی",
  voice_broadcast_id: "آیدی کمپین",
  voice_broadcast_number_id: "آیدی شماره کمپین",
  partner_id: "آیدی شریک کاری",
  pbx_server_id: "آیدی سرور تماس",
  pbx_trunk_id: "آیدی ترانک",
  pbx_channel_reference_code: "کد مرجع کانال",
  number: "شماره",
  extension: "داخلی",
  scheduled_time: "زمان برنامه ریزی شده",
  pickup_time: "زمان برداشت سرور",
  sent_time: "زمان ارسال",
  start_time: "زمان شروع",
  answer_time: "زمان پاسخ",
  end_time: "زمان اتمام",
  unique_id: "شناسه خاص",
  user_field: "عدد ورودی کاربر",
  caller_id: "آیدی تماس گیرنده",
  waiting_seconds: "طول انتظار",
  billable_seconds: "زمان قابل پرداخت",
  duration_seconds: "طول تماس",
  status: "وضعیت",
  created_at: "تاریخ ایجاد",
  updated_at: "تاریخ ویرایش",
  partner: "شریک کاری",
};

const statusFilter = {
  1: null,
  2: BroadCastingNumberStatusEnum.WAITING_FOR_SEND,
  3: BroadCastingNumberStatusEnum.SENDING,
  4: BroadCastingNumberStatusEnum.ANSWERED,
  5: BroadCastingNumberStatusEnum.FAILED,
};

const columnsDetail: ITableSchema[] = [
  {
    key: "status",
    label: "وضعیت",
    minWidth: 170,
    align: "center",
  },
  {
    key: "created_at",
    label: "ایجاد",
    minWidth: 100,
    align: "center",
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },
  {
    key: "pickup_time",
    label: "برداشت",
    minWidth: 100,
    align: "center",
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },
  {
    key: "sent_time",
    label: "تماس",
    minWidth: 100,
    align: "center",
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },
  {
    key: "end_time",
    label: "پایان",
    minWidth: 100,
    align: "center",
    format: (value: string) =>
      value && dateFormatter({ lang: "fa", date: value }),
  },
  {
    key: "billable_seconds",
    label: "مدت مکالمه",
    minWidth: 170,
    align: "center",
  },
  {
    key: "user_field",
    label: "عدد ورودی کاربر",
    minWidth: 130,
    align: "center",
  },
  {
    key: "operations",
    label: "عملیات",
    minWidth: 100,
    align: "center",
  },
];

interface IBroadCastingNumberProps {
  broadcastId: string;
  type?: string;
}

interface IVoiceNumberSchema {
  id: number;
  number: string;
  call_input: string | null;
  total_cast: number;
  tries: number;
  total_talk_time: number | string | null;
  total_waiting_time: number | string | null;

  extension: string | null;
  voice_broadcast_id: number;
  created_at: string | null;
  status: ReactNode;
  operations: string | ReactNode;
}

export default function BroadCastingTabsComponent({
  broadcastId,
  type,
}: Readonly<IBroadCastingNumberProps>) {
  const [value, setValue] = useState("1");
  const { lang } = useLangContext();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [data, setData] = useState<IVoiceNumberSchema[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [_total, _setTotal] = useState(0);
  const [_page, _setPage] = useState(1);
  const [_rowsPerPage, _setRowsPerPage] = useState(10);
  const { queryStringUrl } = useUrl();

  const [waiting, setWaiting] = useState(false);
  const [waiting1, setWaiting1] = useState(false);
  const protectedInstance = useProtectedAxiosInstance();
  const [reload, setReload] = useState(false);
  const { id } = useParams();
  
  const [callDetails, setCallDetails] = useState<ICallDetail[] | undefined>(
    undefined,
  );
  const [pbxCallDetails, setPbxCallDetails] = useState<
    Omit<ICallDetail, "operations"> | undefined
  >(undefined);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const { setNotification } = useNotificationContext();
  const FILTER_STATUS = (tabValue: number):FilterList[] => {
    const filter_fields = {
      1: null,
      2: ["created", "queued", "retry"],
      3: ["waiting_for_send", "sending"],
      4: ["answered"],
      5: [
        "blocked",
        "no_answer",
        "busy",
        "rejected",
        "unreachable",
        "invalid_number",
        "canceled",
        "device_off",
      ],
    };

    const statuses = filter_fields[tabValue as keyof typeof filter_fields];

    if (!statuses) return [{
        column: NumberListColumnFilterable.VOICE_BROADCAST_ID,
        operator: FilterOperatorEnum.EQUAL,
        value: id,
      }];

    return [
      {
        column: NumberListColumnFilterable.VOICE_BROADCAST_ID,
        operator: FilterOperatorEnum.EQUAL,
        value: id,
      },
      {
        column: NumberListColumnFilterable.STATUS,
        operator: FilterOperatorEnum.IN,
        value: statuses,
      },
    ];
  };

  const getData = async ({
    setData,
    isNotNull,
    setWaiting,
  }: {
    setData: React.Dispatch<React.SetStateAction<IVoiceNumberSchema[]>>;
    isNotNull?: boolean;
    setWaiting: React.Dispatch<React.SetStateAction<boolean>>;
  }) => {
    try {
      setWaiting(true);
      const broadcastingDetailsRequest = new NumberListRequest(
        protectedInstance,
      );
      const tabNumber = Number(value);
      const { data } = await broadcastingDetailsRequest.list({
        page,
        count: rowsPerPage,
        filters: FILTER_STATUS(tabNumber),
      });
      setTotal(data.total);
      setData(
        data.voice_broadcast_numbers.map((itm) => ({
          ...itm,
          total_talk_time: ` ${itm.total_talk_time??0} ثانیه`,
          total_waiting_time: ` ${itm.total_waiting_time ??0} ثانیه`,
          status: (
            <InfoBadge
              origin={OriginEnum.BROADCASTING_NUMBER}
              status={itm.status as "pending"}
            />
          ),
          operations: (
            <Stack justifyContent="center" alignItems="center">
              {loading ? (
                <CircularProgress />
              ) : (
                <ViewAction handleView={() => handleView(itm.id)} />
              )}
            </Stack>
          ),
        })),
      );
      setWaiting(false);
    } catch (error: any) {
      errorResolver({ error, setNotification });
      // navigate("/404");
      setWaiting(false);
      console.error(error.message);
    }
  };

  useEffect(() => {
    getData({ setData, setWaiting });
  }, [reload, value, page, rowsPerPage]);

  const infos = compInfos[lang];

  const handleView = async (_id: number) => {
    const callDetailRequest = new CallDetailListRequest(protectedInstance);
    setLoading(true);
    try {
      const {
        data: { pbx_call_detail_records, total },
      } = await callDetailRequest.list({
        count: _rowsPerPage,
        page: _page,
        filters: [
          {
            column: CallDetailListColumnFilterable.VOICE_BROADCAST_NUMBER_ID,
            operator: FilterOperatorEnum.EQUAL,
            value: _id,
          },
        ],
        sorts: [
          {
            column: CallDetailListColumnSortable.ID,
            direction: SortDirectionEnum.DESC,
          },
        ],
      });
      _setTotal(total);      
      setCallDetails(
        pbx_call_detail_records.map((itm) => ({
          ...itm,
          status: (
            <InfoBadge
              origin={OriginEnum.BROADCASTING_NUMBER}
              status={itm.status as "pending"}
            />
          ),
          operations: (
            <Stack
              direction="row"
              gap="1px"
              alignItems="center"
              justifyContent="center"
            >
              <ViewAction handleView={() => setPbxCallDetails(itm)} />
            </Stack>
          ),
        })),
      );
    } catch (error) {
      errorResolver({ error, setNotification });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const tInfo = compInfos[lang];
  const colName = tInfo.columns;

  const _columns: ITableSchema[] = [
    {
      key: "number",
      label: colName.phone,
      minWidth: 170,
      align: "center",
    },
    {
      key: "tries",
      label: colName.tryNumber,
      minWidth: 170,
      align: "center",
    },
    {
      key: "call_input",
      label: "عدد ورودی مخاطب",
      minWidth: 170,
      align: "center",
    },
    {
      label: colName.talkTime,
      minWidth: 170,
      align: "center",
      key: "total_talk_time",
    },
    {
      key: "total_waiting_time",
      label: colName.waitingTime,
      minWidth: 100,
      align: "center",
    },
    // {
    //   key: "created_at",
    //   label: colName.date,
    //   minWidth: 100,
    //   align: "center",
    //   format: (value: string) => value && dateFormatter({ lang, date: value }),
    // },

    {
      key: "status",
      label: colName.status,
      minWidth: 170,
      align: "center",
    },
    {
      key: "operations",
      label: "عملیات",
      minWidth: 170,
      align: "center",
    },
  ];

  const columns =
    type == "simple"
      ? _columns.filter((itm) => itm.key !== "call_input")
      : _columns;

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };
  const _handleChangePage = (newPage: number) => {
    _setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setRowsPerPage(+event.target.value);
    setPage(1);
  };
  const _handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    _setRowsPerPage(+event.target.value);
    _setPage(1);
  };

  const handleExport = async (filterType: string) => {
    setLoading(true);
    try {
      let filters: any = [];
      switch (filterType) {
        case "all":
          filters = [];
          break;
        case "successful":
          filters = [
            {
              column: BroadcastExportListFilters.STATUS,
              operator: FilterOperatorEnum.IN,
              value: ["answered"],
            },
          ];
          break;
        case "unsuccessful":
          filters = [
            {
              column: BroadcastExportListFilters.STATUS,
              operator: FilterOperatorEnum.NOT_IN,
              value: ["created", "waiting_for_send", "sending", "answered"],
            },
          ];
          break;
        case "survey":
          filters = [
            {
              column: BroadcastExportListFilters.CALL_INPUT,
              operator: FilterOperatorEnum.IS_NOT_NULL,
            },
          ];
          break;
      }

      const broadcastingDetailsRequest = new BroadcastingDetailsRequest(
        protectedInstance,
      );

      const {
        data: { url },
      } = await broadcastingDetailsRequest.export(Number(broadcastId), {
        filters,
      });

      window.open(url, "_blank");
    } catch (error) {
      console.error(error);
      errorResolver({ error, setNotification });
    } finally {
      setLoading(false);
      handleClose();
    }
  };

  return (
    <>
      <Box
        sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}
      >
        <Stack
          justifyContent="end"
          direction="row"
          pt="12px"
          px="12px"
          alignItems="center"
        >
          {!waiting ? (
            <>
              <LoadingButton
                variant="activeOutlined"
                onClick={handleClick}
                loading={loading}
                sx={{ gap: "3px", display: "flex", alignItems: "center" }}
              >
                <FileDownloadRounded />
                دانلود گزارش تماس (اکسل)
              </LoadingButton>
              <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                MenuListProps={{
                  sx: {
                    minWidth: "220px",
                    textAlign: "right",
                  },
                }}
              >
                <MenuItem
                  onClick={() => handleExport("all")}
                  sx={{ justifyContent: "flex-end", minWidth: "100%" }}
                >
                  همه
                </MenuItem>
                <MenuItem
                  onClick={() => handleExport("unsuccessful")}
                  sx={{ justifyContent: "flex-end", minWidth: "100%" }}
                >
                  تماس‌های ناموفق
                </MenuItem>
                <MenuItem
                  onClick={() => handleExport("successful")}
                  sx={{ justifyContent: "flex-end", minWidth: "100%" }}
                >
                  تماس‌های موفق
                </MenuItem>
                <MenuItem
                  onClick={() => handleExport("survey")}
                  sx={{ justifyContent: "flex-end", minWidth: "100%" }}
                >
                  گزارش نظرسنجی
                </MenuItem>
              </Menu>
            </>
          ) : (
            <Skeleton variant="rounded" width={300} height={40} />
          )}
        </Stack>
        <TabContext value={value}>
          <Box sx={{ borderBottom: 1, borderColor: "divider", width: "100%" }}>
            <TabList onChange={handleChange}>
              {infos.tabs.map(({ id, label }) => (
                <Tab label={label} key={id} value={id} />
              ))}
            </TabList>
          </Box>
          {Array(5)
            .fill("")
            .map((_, i) => (
              <MuiTabPanel value={`${i + 1}`}>
                <CustomTable
                  reloadAction={() => setReload(!reload)}
                  hasTopPagination
                  hasSearchFilter
                  columns={columns}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  page={page}
                  rows={data}
                  rowsPerPage={rowsPerPage}
                  count={total}
                  // hasWaiting={data.length == 0}
                  hasWaiting={waiting || loading}
                />
              </MuiTabPanel>
            ))}
        </TabContext>
      </Box>
      <MuiBaseModal
        size="lg"
        onClose={() => setCallDetails(undefined)}
        open={!!callDetails}
        title={`جزئیات تماس (${callDetails && callDetails[0]?.number})`}
      >
        {callDetails && (
          <CustomTable
            columns={columnsDetail}
            handleChangePage={_handleChangePage}
            handleChangeRowsPerPage={_handleChangeRowsPerPage}
            page={_page}
            rows={callDetails}
            rowsPerPage={_rowsPerPage}
            count={_total}
            hasWaiting={loading}
          />
        )}
      </MuiBaseModal>
      <MuiBaseModal
        onClose={() => setPbxCallDetails(undefined)}
        open={!!pbxCallDetails}
        title="جزئیات تماس"
      >
        <Stack>
          <Stack
            gap="24px"
            direction="column"
            sx={{
              bgcolor: (theme) => theme.palette.gray[100],
              borderWidth: "1px",
              borderStyle: "solid",
              py: "18px",
              px: "16px",
              borderRadius: "8px",
            }}
          >
            {Object.keys(displayedData).map((itm, i) => (
              <Stack
                key={i}
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                gap="16px"
                flexWrap="wrap"
              >
                <Typography
                  variant="h6MediumBold"
                  color={(theme) => theme.palette.dark[40]}
                >
                  {displayedData[itm as "created_at"]}
                </Typography>
                <Typography
                  variant="h6MediumBold"
                  color={(theme) => theme.palette.dominant.main}
                >
                  {pbxCallDetails && (
                    <>
                      {itm == "partner" ? (
                        <>
                          {pbxCallDetails.partner && (
                            <LinkWithPopover
                              title={pbxCallDetails.partner?.caption ?? ""}
                            >
                              <Stack
                                sx={{
                                  padding: "4px",
                                  borderRadius: "50%",
                                  width: "fit-content",
                                  cursor: "pointer",
                                  justifyContent: "center",
                                  textDecoration: "none",
                                  color: (theme) =>
                                    theme.palette.complement[400],
                                  "&:hover": {
                                    color: (theme) => theme.palette.others.link,
                                    // backgroundColor: (theme) => theme.palette.others.doneBg,
                                  },
                                }}
                                component={Link}
                                to={queryStringUrl(routerPaths.PARTNERS, {
                                  filters: [
                                    {
                                      column: PartnerListColumnFilterable.ID,
                                      operator: FilterOperatorEnum.EQUAL,
                                      value: pbxCallDetails?.partner?.id ?? "1",
                                    },
                                  ],
                                })}
                              >
                                {pbxCallDetails?.partner?.caption}
                              </Stack>
                            </LinkWithPopover>
                          )}
                        </>
                      ) : itm == "status" ? (
                        <InfoBadge
                          origin={OriginEnum.BROADCASTING_NUMBER}
                          status={
                            (pbxCallDetails?.status ?? "pending") as "pending"
                          }
                        />
                      ) : (
                        <>
                          {itm.includes("at") || itm.includes("time")
                            ? pbxCallDetails[itm as "created_at"] &&
                              dateFormatter({
                                lang: "fa",
                                date: pbxCallDetails[itm as "created_at"],
                              })
                            : pbxCallDetails[itm as "created_at"]}
                        </>
                      )}
                    </>
                  )}
                </Typography>
              </Stack>
            ))}
          </Stack>
          <Stack
            width="100%"
            direction="row"
            justifyContent={{ xs: "center", sm: "end" }}
            mb="16px"
          ></Stack>
        </Stack>
      </MuiBaseModal>
    </>
  );
}
